import { Inject, Injectable } from "@angular/core";
import { idbConfig } from "src/constants/idb-config";

const { objectStoreName } = idbConfig;

@Injectable({
  providedIn: "root",
})
export class IDBService {
  constructor(
    @Inject("idbInstance") private dbInstance: Promise<IDBDatabase>
  ) {}

  /**
   * Method that stores data in indexedDB against the specified id.
   * @param id - ID against which data is to be stored.
   * @param data - Data to be stored.
   * @returns Promise indicating whether the transaction is successful or not.
   */
  async storeData(id: string, data: any) {
    if (!this.dbInstance) {
      return;
    }

    const db = await this.dbInstance;

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(objectStoreName, "readwrite");
      const objectStore = transaction.objectStore(objectStoreName);

      const addDataReq = objectStore.add({ id, data });

      addDataReq.onsuccess = () => {
        resolve(`Data stored successfully to ${id}`);
      };

      addDataReq.onerror = () => {
        if (addDataReq.error?.name !== "ConstraintError") {
          reject(addDataReq.error);
          return;
        }

        const newTxn = db.transaction(objectStoreName, "readwrite");
        const reOpenObjStore = newTxn.objectStore(objectStoreName);
        const putDataReq = reOpenObjStore.put({ id, data });

        putDataReq.onsuccess = () => {
          resolve(`Data updated successfully to ${id}`);
        };

        putDataReq.onerror = () => {
          reject(putDataReq.error);
        };
      };
    });
  }

  /**
   * Method used to fetch data from indexedDB.
   * @param id - ID of the data to be fetched.
   * @returns Data which is stored against the specified ID.
   */
  async fetchData<T>(id: string) {
    if (!this.dbInstance) {
      return;
    }

    const db = await this.dbInstance;

    return new Promise<T>((resolve, reject) => {
      const transaction = db.transaction(objectStoreName, "readonly");
      const objectStore = transaction.objectStore(objectStoreName);

      const getRequest = objectStore.get(id);

      getRequest.onsuccess = () => {
        resolve(<T>getRequest.result?.data);
      };

      getRequest.onerror = () => {
        reject(getRequest.error);
      };
    });
  }
}
